import {default as Pie2DDataset} from '../_internal/datasets/pie2d';
import CommonAPI from './commonchartapi';
import { pluck, pluckNumber, componentFactory,
  POSITION_BOTTOM, POSITION_RIGHT, ZEROSTRING,
  ONESTRING } from '../_internal/lib/lib';
import { convertColor } from '../_internal/lib/lib-graphics';
import Caption from '../_internal/components/caption';
import SubCaption from '../_internal/components/sub-caption';
import Background from '../_internal/components/background';
import datasetFactory from '../_internal/factories/pie-dataset';
import legendItemFactory from '../_internal/factories/legend';
import { _manageLegendSpace } from '../_internal/common-chart-api/legend-spacemanager';
import { priorityList } from '../_internal/schedular';

let math = Math,
  mathMin = math.min,
  mathMax = math.max,
  mathAbs = math.abs,
  mathPI = math.PI,
  mathRound = math.round,
  deg2rad = mathPI / 180,
  rad2deg = 180 / mathPI,
  count = 0,
  /**
   * Slices the dataset
   * @param {Object} dataset The dataset on which to perform the slicing operation
   * @param {number} index The index of the data within the dataset which will be sliced
   * @param {boolean} slice If true, the dataset is sliced out. Else, the dataset is sliced in.
   *
   * @return {boolean} Returns the slicing state of the data at the given index after slicing
   */
  performSlicing = (dataset, index, slice) => {
    let sliceVal = !!slice,
      /**
       * If the user as not explicitly provided the slice parameter OR if the provided slice
       * parameter is not the same as the present slice state of the given data plot, the data plot
       * is eligible for slicing.
       *
       * @param {Object} dataPlot The dataplot which needs to be checked for slicing eligibility
       *
       * @return {boolean} Whether the given data plot will slice or not
       */
      willSlice = dataPlot => sliceVal !== dataPlot.config.sliced || typeof slice === 'undefined',
      data,
      dataConfig,
      output,
      selectedDataPlot;

    if (!dataset) {
      return output;
    }

    // Extract the all data from the dataset
    data = (dataset.components && dataset.components.data) || [];
    // Check if dataset is reversed and adjust the user's given index accordingly
    index = dataset.config.reversePlotOrder ? (data.length - index - 1) : index;
    selectedDataPlot = data[index];

    if (selectedDataPlot) {
      dataConfig = selectedDataPlot.config;
      // If the data is eligible for slicing, slice it. Else, return the present slice state of the
      // data plot
      if (willSlice(selectedDataPlot)) {
        output = dataset.plotGraphicClick.call(selectedDataPlot.graphics.element);
      } else {
        output = dataConfig.sliced;
      }
    }

    return output;
  };

class Pie2D extends CommonAPI {
  /**
   * Provides the name of the chart extension
   *
   * @static
   * @return {string} The name of the chart extension
   */
  static getName () {
    return 'Pie2D';
  }

  constructor () {
    super();
    this.defaultSeriesType = 'pie';
    this.defaultPlotShadow = 1;
    this.reverseLegend = 1;
    this.defaultPaletteOptions = undefined;
    this.sliceOnLegendClick = true;
    this.dontShowLegendByDefault = true;
    this.defaultZeroPlaneHighlighted = false;
    this.hasCanvas = true;
    this.eiMethods = /** @lends FusionCharts# */ {
      /**
       * Pie charts have slices that can be clicked to slice in and out.
       * Checks whether a particular wedge of Pie or Doughnut chart is sliced-out or sliced-in.
       *
       * > Available on `pie` and `doughnut` chart types only.
       *
       * @group chart:pie-slice
       *
       * @param {number} index - The index of the data corresponding to the pie/doughnut slice.
       * @return {boolean} - The sliced state of the pie/doughnut wedge. Returns `true` if it's sliced out,
       * or `false` if it's sliced in.
       *
       * @example
       * // Render a pie 2d chart with some data in sliced out state, provide data index
       * // in an input textfield and get the sliced state of the pie on click of a button
       * FusionCharts.ready(function () {
       *     var chart = new FusionCharts({
       *         type: "pie2d",
       *         renderAt: "chart-container",
       *         dataSource: "data.json",
       *         dataFormat: "jsonurl"
       *     }).render();
       *
       *     // Get the sliced state of a pie returned when clicked on a button
       *     // (with an id pie-sliced-state). It picks the data index from
       *     // an input textfield (with id pie-data-index).
       *     document.getElementById("pie-sliced-state").onclick = function () {
       *         var dataIndex = document.getElementById("pie-data-index").value,
       *             slicedState = chart.isPlotItemSliced(dataIndex);
       *     };
       * });
       */
      isPlotItemSliced: function (index) {
        var data,
          config,
          apiInstance = this.apiInstance,
          dataset = apiInstance && apiInstance.getDatasets();

        return dataset && (dataset = dataset[0]) && (data = dataset.components.data) && data[index] &&
          (config = data[index].config) && config.sliced;
      },

      addData: function () {
        var apiInstance = this.apiInstance,
          dataset = apiInstance && apiInstance.getDatasets();
        return dataset && (dataset = dataset[0]) && dataset.addData.apply(dataset, arguments);
      },

      removeData: function () {
        var apiInstance = this.apiInstance,
          dataset = apiInstance && apiInstance.getDatasets();
        return dataset && (dataset = dataset[0]) && dataset.removeData.apply(dataset, arguments);
      },

      updateData: function () {
        var apiInstance = this.apiInstance,
          dataset = apiInstance && apiInstance.getDatasets();
        return dataset && (dataset = dataset[0]) && dataset.updateData.apply(dataset, arguments);
      },
      /**
       * Pie charts have slices. These slices can be clicked by users to slice in or slice out.
       * Slices a pie/doughnut wedge to in / out state. In absence of the optional second parameter, it
       * toggles the sliced state of the pie. The second parameter only enforces a specific sliced state.
       *
       * > Available on `pie` and `doughnut` chart types only.
       *
       * @group chart:pie-slice
       *
       * @param {number} index - The index of the data corresponding to the pie/doughnut slice.
       * @param {boolean=} [slice] - Gives direction to chart on what is the required sliced state. For
       * `true`, it slices out, if in sliced-in state. Or else, maintains it's sliced-out state. And
       * vice-versa.
       * @param {Function} [callback] - If the chart is in asyncRender mode, the callback function
       * is invoked with the result of the slicing operation.
       *
       * @return {boolean} - The final sliced state of the pie/doughnut wedge. Returns `true` if it's
       * sliced out, or `false` if it's sliced in. If the chart is in asyncRender mode, this method
       * returns `undefined` and instead the callback provided is invoked with the result of the
       * slicing operation.
       *
       * @fires FusionCharts#slicingStart
       * @fires FusionCharts#slicingEnd
       *
       * @example
       * // Render a pie 2d chart, provide data index in an input textfield
       * // and toggle the sliced state of the pie on click of a button
       * FusionCharts.ready(function () {
       *     var chart = new FusionCharts({
       *         type: "pie2d",
       *         renderAt: "chart-container",
       *         dataSource: "data.json",
       *         dataFormat: "jsonurl"
       *     }).render();
       *
       *     // Toggle the sliced state of the pie when clicked on a button
       *     // (with an id pie-sliced-state). It picks the data index from
       *     // an input textfield (with id pie-data-index).
       *     document.getElementById("pie-sliced-state").onclick = function () {
       *         var dataIndex = document.getElementById("pie-data-index").value;
       *         chart.slicePlotItem(dataIndex);
       *     };
       * });
       */
      slicePlotItem: function (index, slice, callback) {
        var fcInstance = this,
          apiInstance = fcInstance.apiInstance;

        if (callback) {
          apiInstance.addJob(`eiMethods-slice-plot${count++}`, function () {
            let slicingResult = performSlicing(apiInstance.getDatasets()[0], index, slice);
            return (typeof callback === 'function') && callback(slicingResult);
          }, priorityList.postRender);
        } else {
          return performSlicing(apiInstance.getDatasets()[0], index, slice);
        }
      },
      /**
       * Rotates the pie/doughnut chart to a specific angle or by a specific angle. The mode of
       * operation is controlled by the optional second parameter. Even the first parameter is optional,
       * in absence of which, the chart doesn't rotate and simply returns the current starting angle
       * of the pie/doughnut chart.
       *
       * Starting angle of a pie/doughnut chart is the angle at which the starting face of the first data is
       * aligned to. Each pie is drawn in counter clock-wise direction.
       *
       * > Available on `pie` and `doughnut` chart types only.
       *
       * @group chart:pie-slice
       *
       * @param {degrees=} [angle=0] - The angle by which to rotate the entire pie/doughnut chart.
       * @param {boolean=} [relative=false] - Specify whether the angle being set is relative to the current
       * angle or with respect to absolute 0.
       * @return {degrees} - The final state of the starting angle of the chart.
       *
       * @example
       * // Render a pie 2d chart and rotate the chart by 90 degrees on click of a button
       * FusionCharts.ready(function () {
       *     var chart = new FusionCharts({
       *         type: "pie2d",
       *         renderAt: "chart-container",
       *         dataSource: "data.json",
       *         dataFormat: "jsonurl"
       *     }).render();
       *
       *     // Assign the functionality of rotating the chart by 90 degrees when clicked on
       *     // a button (with an id rotate-chart).
       *     document.getElementById("rotate-chart").onclick = function () {
       *         chart.startingAngle(90, true);
       *     };
       * });
       */
      startingAngle: function (angle, relative, callback) {
        var chart = this.apiInstance,
          output;

        if (callback) {
          chart.addJob(`eiMethods-start-angle${count++}`, function () {
            output = chart._startingAngle(angle, relative);
            if (typeof callback === 'function') {
              callback(output);
            }
          }, priorityList.postRender);
        } else {
          return chart._startingAngle(angle, relative);
        }
      }
    };
    this.registerFactory('dataset', datasetFactory, ['vCanvas', 'legend']);
    this.registerFactory('legend', legendItemFactory);
  }
  __setDefaultConfig () {
    super.__setDefaultConfig();
    let config = this.config;
    config.alignCaptionWithCanvas = 0;
    config.formatnumberscale = 1;
    config.isSingleSeries = true;
    config.friendlyName = 'Pie Chart';
    config.defaultDatasetType = 'Pie2D';
    config.plotborderthickness = 1;
    config.decimals = 2;
    config.alphaanimation = 0;
    config.singletonPlaceValue = true;
    config.usedataplotcolorforlabels = 0;
    config.enableslicing = ONESTRING;
    config.skipCanvasDrawing = true;
  }

  /**
   * Parse the chart attributes and store in chart's config
   * @param {Object} dataObj User input json
   */
  parseChartAttr (dataObj) {
    super.parseChartAttr(dataObj);
    let iapi = this,
      chartAttrs = iapi.getFromEnv('chart-attrib');
    iapi.config.showLegend = pluckNumber(chartAttrs.showlegend, 0);
  }
  /**
   * function to create component and configure and also configure the chart
   * It creates components which are depend on data and configure them and also configure chart
   * It calls helper function namely, configure
   * @param  {Object} dataObj contains json data of the chart
   */
  configureAttributes (dataObj) {
    var iapi = this,
      chartConfig = iapi.config,
      toolTipController;
    iapi.parseChartAttr(dataObj);
    iapi.createComponent(dataObj);
    iapi.config.skipConfigureIteration.axis = true;

    iapi.configureChildren();
    // @TODO this is not proper place to invoking this fn
    // as configureAttributes fn is copied from the mscartesian to
    // override the parent configureAttributes fn, this _createToolBox
    // fn invoking is still here.
    iapi._createToolBox();
    toolTipController = iapi.getFromEnv('toolTipController');
    toolTipController.setStyle({'bgColor': convertColor((chartConfig.tooltipbgcolor || 'FFF'), (chartConfig.tooltipbgalpha || 100)),
      'rawBgColor': (chartConfig.tooltipbgcolor || 'FFF').replace(/^#?([a-f0-9]+)/ig, '#$1'),
      'fontColor': (chartConfig.tooltipcolor || chartConfig.basefontcolor || '545454').replace(/^#?([a-f0-9]+)/ig, '#$1'),
      'borderColor': convertColor((chartConfig.tooltipbordercolor || '666'), (chartConfig.tooltipborderalpha || 100)),
      'rawBorderColor': (chartConfig.tooltipbordercolor || '666').replace(/^#?([a-f0-9]+)/ig, '#$1'),
      'bgAlpha': pluckNumber(chartConfig.tooltipbgalpha, 100),
      'borderThickness': pluckNumber(chartConfig.tooltipborderthickness, 1),
      'showToolTipShadow': pluckNumber(chartConfig.showtooltipshadow || 0),
      'borderRadius': pluckNumber(chartConfig.tooltipborderradius, 0),
      'font-size': chartConfig.basefontsize || 10,
      'font-family': chartConfig.basefont || this.getFromEnv('style').inCanfontFamily,
      'padding': pluckNumber(chartConfig.tooltippadding || 3),
      'borderAlpha': pluckNumber(chartConfig.tooltipborderalpha, 100)});
  }
  /**
   * function to create chart level containers
   */
  _createLayers () {
    super._createLayers();
    let animationManager = this.getFromEnv('animationManager');
    !this.getChildContainer('legendGroup') &&
      this.addChildContainer('legendGroup', animationManager.setAnimation({
        el: 'group',
        attr: {name: 'legend'},
        component: this,
        container: this.getContainer('parentgroup'),
        label: 'group'
      }));
  }
  /**
   * Create child components of chart
   * @param {Object} dataObj User input json
   */
  createComponent () {
    let iapi = this,
      skipConfigureIteration;

    skipConfigureIteration = iapi.config.skipConfigureIteration = {};
    iapi.createBaseComponent();
    componentFactory(iapi, Caption, 'caption');
    skipConfigureIteration.caption = true;
    componentFactory(iapi, SubCaption, 'subCaption');
    skipConfigureIteration.subCaption = true;
    componentFactory(iapi, Background, 'background');
    skipConfigureIteration.background = true;
    skipConfigureIteration.canvas = true;
    // Create dynamic components like message logger
    iapi._createConfigurableComponents && iapi._createConfigurableComponents();

    // Update alert manager
    if (iapi.config.realtimeEnabled) {
      iapi._realTimeConfigure && iapi._realTimeConfigure();
      // if (AlertManagerClass) {
      //   componentFactory(iapi, AlertManagerClass, 'alertManager');
      // }
    }
  }
  /**
   * function to calculate post space management calculations
   * calculations like axis dimenetion set, allocate space for xaxis labels,
   * canvas padding etc.
   * As pie charts do not have axis and canvas so this api will override the parent api
   */
  // eslint-disable-next-line
  _postSpaceManagement () {
    this.config.showLegend && this.getChildren('legend') && this.getChildren('legend')[0].postSpaceManager();
  }

  /**
   * function to check if the chart specific data is proper is not
   * this fn is define for specific chart types
   * @return {boolean} if JSON data is valid or not
   */
  // eslint-disable-next-line
  _checkInvalidSpecificData () {
    var chart = this,
      chartAttr = chart.getFromEnv('dataSource'),
      i,
      len,
      zeroSum = 0,
      nullSum = 0,
      data = chartAttr.data,
      value;

    /**
     * Case 1: If no data is present, return true
     * Case 2: If no valid data is present(ie, data has all zero and/or null values)
     */
    if (!data) {
      return true;
    } else {
      len = data.length || 0;
      for (i = 0; i < len; i++) {
        value = Number(data[i].value);
        zeroSum += (!isNaN(value) && value === 0) ? 1 : 0;
        nullSum += isNaN(value) ? 1 : 0;
      }

      // Number of zero values + number of null values >= total data present.
      if (zeroSum + nullSum >= len) {
        return true;
      }
    }

    return false;
  }

  _spaceManager () {
    var chart = this,
      chartConfig = chart.config,
      dataSet = chart.getChildren('dataset')[0],
      data = dataSet.components.data,
      conf = dataSet.config,
      legend = chart.getFromEnv('legend'),
      colorM = chart.getFromEnv('color-manager'),
      SmartLabel = chart.getFromEnv('smartLabel'),
      textWidthArr = [],
      length = conf.dataLabelCounter,
      labelMaxW = 0,
      fcJSONChart = chart.getFromEnv('dataSource').chart,
      manageLabelOverflow = pluckNumber(fcJSONChart.managelabeloverflow, 0),
      userGivenSlicingDist = pluckNumber(fcJSONChart.slicingdistance),
      slicingDistance = !conf.preSliced && (chartConfig.allPlotSliceEnabled === ZEROSTRING) &&
                  (fcJSONChart.showlegend !== ONESTRING || fcJSONChart.interactivelegend === ZEROSTRING) ?
        0 : mathAbs(pluckNumber(userGivenSlicingDist, 20)),
      pieRadius = pluckNumber(fcJSONChart.pieradius, 0),
      enableSmartLabels = pluckNumber(fcJSONChart.enablesmartlabels, fcJSONChart.enablesmartlabel, 1),
      skipOverlapLabels = enableSmartLabels ? pluckNumber(fcJSONChart.skipoverlaplabels,
        fcJSONChart.skipoverlaplabel, 1) : 0,
      isSmartLineSlanted = pluckNumber(fcJSONChart.issmartlineslanted, 1),
      labelDistance = length ? pluckNumber(fcJSONChart.labeldistance, fcJSONChart.nametbdistance, 5) :
        slicingDistance,
      smartLabelClearance = pluckNumber(fcJSONChart.smartlabelclearance, 5),
      width = chartConfig.width,
      height = chartConfig.height,
      actionBarHeight = (chart._manageActionBarSpace(height * 0.225) || {}).bottom,
      chartWorkingWidth = width - (chartConfig.marginRight + chartConfig.marginLeft),
      chartWorkingHeight = height - (chartConfig.marginTop + chartConfig.marginBottom) -
                  (actionBarHeight ? actionBarHeight + chartConfig.marginBottom : 0),
      minOfWH = mathMin(chartWorkingHeight, chartWorkingWidth),
      smartLineColor = pluck(fcJSONChart.smartlinecolor, colorM.getColor('plotFillColor')),
      smartLineAlpha = pluckNumber(fcJSONChart.smartlinealpha, 100),
      smartLineThickness = pluckNumber(fcJSONChart.smartlinethickness, 0.7),
      dataLabelOptions = (conf.dataLabelOptions = dataSet._parseDataLabelOptions()),
      style = dataLabelOptions.style,
      lineHeight = length ? pluckNumber(parseInt(style.lineHeight, 10), 12) : 0, // 2px padding
      pieMinRadius = pieRadius === 0 ? minOfWH * 0.15 : pieRadius,
      pieMinDia = (2 * pieMinRadius),
      legendSpace = {
        bottom: 0,
        right: 0
      },
      captionSpace,
      pieYScale = conf.pieYScale,
      pieSliceDepth = conf.pieSliceDepth,
      textObj,
      avaiableMaxpieSliceDepth,
      totalSpaceReq;
    // Old code for placeValuesInside
    // placeLabelsInside = pluckNumber ((FCchartName === 'doughnut2d') ? 0 : fcJSONChart.placevaluesinside),

    dataLabelOptions.connectorWidth = smartLineThickness;
    dataLabelOptions.connectorPadding = pluckNumber(fcJSONChart.connectorpadding, 5);
    dataLabelOptions.connectorColor = convertColor(smartLineColor, smartLineAlpha);

    // If smart label is on and there is a label defined only then modify the label distance
    if (length) {
      if (enableSmartLabels) {
        labelDistance = smartLabelClearance;
      }
      labelDistance += slicingDistance;
    }
    // Include label
    totalSpaceReq = pieMinDia + ((lineHeight + labelDistance) * 2);

    // Provide at least single line height space for caption.
    // a space manager that manages the space for the tools as well as the captions.
    captionSpace = chart._manageChartMenuBar(totalSpaceReq < chartWorkingHeight ?
      chartWorkingHeight - totalSpaceReq : chartWorkingHeight / 2);
    chartWorkingHeight -= ((captionSpace.top || 0) + (captionSpace.bottom || 0));

    if (conf.showLegend) {
      chart.config.hasLegend = true;
      if (pluck(fcJSONChart.legendposition, POSITION_BOTTOM).toLowerCase() !== POSITION_RIGHT) {
        legendSpace = legend._manageLegendPosition(chartWorkingHeight / 2);
        chartWorkingHeight -= legendSpace.bottom;
      } else {
        legendSpace = legend._manageLegendPosition(chartWorkingHeight / 2);
        chartWorkingWidth -= legendSpace.right;
      }
    }
    chart._allocateSpace(legendSpace);
    // Now get the max width required for all display text
    // set the style
    SmartLabel.useEllipsesOnOverflow(chartConfig.useEllipsesWhenOverflow);
    if (length !== 1) { // Fix for single data in Pie makes pie very small in size.
      for (; length--;) {
        SmartLabel.setStyle(data[length].config.style || chartConfig.dataLabelStyle);
        textWidthArr[length] = textObj = SmartLabel.getOriSize(data[length].config.displayValue);
        labelMaxW = mathMax(labelMaxW, textObj.width);
      }
    }
    // If redius not supplyed then auto calculate it
    if (pieRadius === 0) {
      pieMinRadius = chart._stubRadius(chartWorkingWidth, labelMaxW, chartWorkingHeight, labelDistance,
        slicingDistance, lineHeight, pieMinRadius);
    } else {
      conf.slicingDistance = slicingDistance;
      conf.pieMinRadius = pieMinRadius;
      dataLabelOptions.distance = labelDistance;
    }
    avaiableMaxpieSliceDepth = chartWorkingHeight - (2 * ((pieMinRadius * pieYScale) + lineHeight));
    conf.managedPieSliceDepth = (pieSliceDepth > avaiableMaxpieSliceDepth) ? (pieSliceDepth -
              avaiableMaxpieSliceDepth) : conf.pieSliceDepth;
    dataLabelOptions.isSmartLineSlanted = isSmartLineSlanted;
    dataLabelOptions.enableSmartLabels = enableSmartLabels;
    dataLabelOptions.skipOverlapLabels = skipOverlapLabels;
    dataLabelOptions.manageLabelOverflow = manageLabelOverflow;
  }
  // manages the spaces when no radius is given.
  _stubRadius (chartWorkingWidth, labelMaxW, chartWorkingHeight, labelDistance, slicingDistance,
    lineHeight, pieMinRadius) {
    var chart = this,
      dataSet = chart.getChildren('dataset')[0],
      conf = dataSet.config,
      fcJSONChart = chart.getFromEnv('dataSource').chart,
      userGivenSlicingDist = pluckNumber(fcJSONChart.slicingdistance),
      dataLabelOptions = conf.dataLabelOptions ||
                  (conf.dataLabelOptions = dataSet._parseDataLabelOptions()),
      availableRadius = 0,
      // Slicing distance can not be less then the MINSLICINGDIST (10)
      MINSLICINGDIST = 10,
      shortFall;
    availableRadius = mathMin((chartWorkingWidth / 2) - labelMaxW - slicingDistance,
      (chartWorkingHeight / 2) - lineHeight) - labelDistance;
    if (availableRadius >= pieMinRadius) { // there has space for min width
      pieMinRadius = availableRadius;
    } else if (!userGivenSlicingDist) { /** @todo smartyfy Labels */
      // If slicing distance is not given by the user, adjust slicing distance based on pie radius.
      shortFall = pieMinRadius - availableRadius;
      // Now reduce the pie slicing distance, but restrict the minimum slicing distance to 10 pixels.
      slicingDistance = labelDistance = mathMax(mathMin(labelDistance - shortFall,
        slicingDistance), MINSLICINGDIST);
    }
    conf.slicingDistance = slicingDistance;
    conf.pieMinRadius = pieMinRadius;
    dataLabelOptions.distance = labelDistance;

    return pieMinRadius;
  }
  _startingAngle (angle, relative) {
    var ang,
      chart = this,
      dataSet = chart.getChildren('dataset')[0],
      seriesData = dataSet.config,
      // Angle is differently handled in Pie2D and Pie3D. So, angles is converted
      // accordingly to the same base. Its radian in 2D while in degrees in 3D.
      // Moreover, sense of positive angle is opposite in the two.
      currentAngle = (ang = seriesData.startAngle) * (-rad2deg) +
                  ((-1) * ang < 0 ? 360 : 0);
    if (!isNaN(angle)) {
      if (!(seriesData.singletonCase || seriesData.isRotating)) {
        angle += relative ? currentAngle : 0;
        seriesData.startAngle = -angle * deg2rad;
        dataSet._rotate(angle);
        currentAngle = angle;
      }
    }
    // Angle normalised in the range of [0, 360]
    return mathRound(((currentAngle %= 360) + (currentAngle < 0 ? 360 : 0)) * 100) / 100;
  }

  /**
   * function to calculate and allote space for legend
   */
  _manageLegendSpace () {
    _manageLegendSpace.call(this);
  }

  getDSdef () {
    return Pie2DDataset;
  }
}
export default Pie2D;
